import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { MailboxInfo } from 'owa-client-types';
import { isConsumer } from 'owa-session-store';
import {
    lazyFetchMicrosoft365UpgradeEligibility,
    lazyGetMicrosoft365UpgradeTier,
} from 'owa-header-microsoft365-upsell';
import { shouldShowUpsellOption } from 'owa-storage-common/lib/utils/shouldShowUpsellOption';

/**
 * The upsell shown by the persistent Microsoft 365 header button, or `hidden` when it should not
 * show at all. Only consumer accounts are ever eligible; within those, two independent upsells
 * share the one button, each behind its own flight:
 *  - `buy`: "Buy Microsoft 365" (header-persistentBuyM365Upsell) for consumers with no subscription.
 *  - `upgradeBasic`: "Upgrade your plan" for Microsoft 365 Basic subscribers.
 *  - `upgradePersonalOrFamily`: the same button for Personal or Family subscribers.
 * Both upgrade modes are gated by header-persistentUpgradeM365Upsell but route to separate DIME
 * configurations.
 */
export type Microsoft365UpsellMode = 'hidden' | 'buy' | 'upgradeBasic' | 'upgradePersonalOrFamily';

type Microsoft365UpgradeTier = 'basic' | 'personalOrFamily';

let getMicrosoft365UpgradeTier:
    | ((mailboxInfo: MailboxInfo) => Microsoft365UpgradeTier | undefined)
    | undefined;

export function isMicrosoft365UpgradeMode(mode: Microsoft365UpsellMode): boolean {
    return mode === 'upgradeBasic' || mode === 'upgradePersonalOrFamily';
}

/**
 * Single source of truth for the Microsoft 365 header upsell. Visibility, label, tooltip, and the
 * launched Dime experience all derive from this so they can never disagree (e.g. showing "Buy" copy
 * while routing an "Upgrade" click).
 *
 * The upgrade modes take precedence over `buy`. Their subscription selector is cached after the
 * header's asynchronous visibility check initializes each account, keeping the premium
 * subscription chain out of AppBoot while label, tooltip, and click routing remain synchronous.
 */
export function getMicrosoft365UpsellMode(mailboxInfo: MailboxInfo): Microsoft365UpsellMode {
    // Only consumer accounts are ever offered the Buy/Upgrade upsell; gate before any flight check.
    if (!isConsumer(undefined /* smtpAddress */, mailboxInfo)) {
        return 'hidden';
    }

    if (isGlobalFeatureEnabled('header-persistentUpgradeM365Upsell')) {
        const upgradeTier = getMicrosoft365UpgradeTier?.(mailboxInfo);
        if (upgradeTier === 'basic') {
            return 'upgradeBasic';
        }
        if (upgradeTier === 'personalOrFamily') {
            return 'upgradePersonalOrFamily';
        }
    }

    if (
        isGlobalFeatureEnabled('header-persistentBuyM365Upsell') &&
        shouldShowUpsellOption(mailboxInfo, false /* isMSQ */)
    ) {
        return 'buy';
    }

    return 'hidden';
}

export function isMicrosoft365UpsellVisible(mailboxInfo: MailboxInfo): boolean | Promise<boolean> {
    // Only consumer accounts are ever offered the upsell; short-circuit before checking any flight
    // or loading the eligibility chunk for non-consumer accounts.
    if (!isConsumer(undefined /* smtpAddress */, mailboxInfo)) {
        return false;
    }

    if (isGlobalFeatureEnabled('header-persistentUpgradeM365Upsell')) {
        // Prime the subscription store, then grab the selector from the same chunk (already loaded,
        // so no extra fetch) and cache it for the synchronous label/tooltip/click reads.
        return lazyFetchMicrosoft365UpgradeEligibility
            .import()
            .then(fetchEligibility => fetchEligibility(mailboxInfo))
            .then(() => lazyGetMicrosoft365UpgradeTier.import())
            .then(getUpgradeTier => {
                getMicrosoft365UpgradeTier = getUpgradeTier;
                return getMicrosoft365UpsellMode(mailboxInfo) !== 'hidden';
            });
    }

    return getMicrosoft365UpsellMode(mailboxInfo) !== 'hidden';
}
